import { useState, useEffect, useRef } from 'react';
import './App.css';

// React Icons - using stable, well-known sets
import { FaInstagram, FaFacebook, FaYelp, FaBeer, FaLeaf, FaHome, FaGlobeAmericas, FaStar, FaPhone, FaMapMarkerAlt, FaShoppingBasket, FaUtensils, FaPepperHot, FaHamburger } from 'react-icons/fa';
import { MdLocalFireDepartment, MdLocationOn, MdPhone, MdDeliveryDining } from 'react-icons/md';
import { GiTacos, GiSandwich, GiAvocado, GiMeat, GiChickenOven } from 'react-icons/gi';

// Scroll fade-in hook
function useFadeIn() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(22px)';
    el.style.transition = 'opacity .55s ease, transform .55s ease';
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.style.opacity = '1';
        el.style.transform = 'translateY(0)';
        obs.disconnect();
      }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

function FadeIn({ children, className, style }) {
  const ref = useFadeIn();
  return <div ref={ref} className={className} style={style}>{children}</div>;
}

// ── NAV ──
function Nav() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <nav className="nav">
        <a href="#" className="nav-logo">
          <img src="/logo.png" alt="Food Bowl Market" />
          <span className="nav-logo-sub">South Park, San Diego</span>
        </a>
        <ul className="nav-links">
          <li><a href="#about">Our Story</a></li>
          <li><a href="#offerings">Shop</a></li>
          <li><a href="#deli">Hot Food</a></li>
          <li><a href="#catering">Catering</a></li>
          <li><a href="#info">Hours</a></li>
          <li><a href="https://www.grubhub.com/restaurant/food-bowl-market-and-deli-3035-cedar-st-san-diego/1660382" target="_blank" rel="noreferrer" className="nav-cta">Order Now ↗</a></li>
        </ul>
        <button className="hamburger" onClick={() => setOpen(o => !o)} aria-label="Menu">
          <span /><span /><span />
        </button>
      </nav>
      <div className={`mobile-menu${open ? ' open' : ''}`}>
        {[['#about','Our Story'],['#offerings','Shop'],['#deli','Hot Food & Deli'],['#info','Hours & Contact'],['#catering','Catering']].map(([href, label]) => (
          <a key={href} href={href} onClick={() => setOpen(false)}>{label}</a>
        ))}
        <a href="https://www.grubhub.com/restaurant/food-bowl-market-and-deli-3035-cedar-st-san-diego/1660382" target="_blank" rel="noreferrer">Order Online ↗</a>
      </div>
    </>
  );
}

// ── HERO ──
function Hero() {
  return (
    <section className="hero">
      <div className="hero-text">
        <div className="hero-badge"><FaStar /> South Park's Neighborhood Grocer</div>
        <h1>Fresh Food,<br /><em>Real Community.</em></h1>
        <p className="hero-sub">Since 1989, Food Bowl Market has been feeding the families of South Park and Golden Hill with fresh produce, quality meats, hot food, and everything in between.</p>
        <div className="hero-buttons">
          <a href="#deli" className="btn-primary"><MdLocalFireDepartment /> Hot Food &amp; Deli</a>
          <a href="https://maps.google.com/?q=Food+Bowl+Market+3035+Cedar+St+San+Diego+CA" target="_blank" rel="noreferrer" className="btn-outline"><MdLocationOn /> Find Us</a>
        </div>
      </div>
      <div className="hero-visual">
        <div className="hero-card">
          <div className="hero-card-icon"><FaLeaf /></div>
          <h3>Fresh Produce Daily</h3>
          <p>Local and organic fruits and vegetables, <span style={{color: 'var(--red-lt)'}}>brought fresh every morning</span>.</p>
        </div>
        <div className="hero-card-row">
          <div className="hero-card">
            <div className="hero-card-icon"><GiMeat /></div>
            <h3>Quality Meats</h3>
            <p>Butcher counter that features <span style={{color: 'var(--red-lt)'}}>USDA Choice</span>.</p>
          </div>
          <div className="hero-card">
            <div className="hero-card-icon"><FaBeer /></div>
            <h3>Craft Beer & Wine</h3>
            <p><span style={{color: 'var(--red-lt)'}}>Hundreds</span> of craft beers and curated wines.</p>
          </div>
        </div>
        <div className="hero-card">
          <div className="hero-card-icon"><FaUtensils /></div>
          <h3>Full-Service Deli</h3>
          <p>Tacos, burritos, tortas, sandwiches, <span style={{color: 'var(--red-lt)'}}>made to order</span>.</p>
        </div>
      </div>
    </section>
  );
}

// ── MARQUEE ──
const marqueeItems = [
  { label: 'Fresh Produce', icon: <FaLeaf /> },
  { label: 'Quality Meats & Butcher', icon: <GiMeat /> },
  { label: 'Tacos & Burritos', icon: <GiTacos /> },
  { label: 'Craft Beer & Wine', icon: <FaBeer /> },
  { label: 'Organic Groceries', icon: <FaLeaf /> },
  { label: 'Household Essentials', icon: <FaHome /> },
  { label: 'Fresh Guacamole & Salsa', icon: <GiAvocado /> },
  { label: 'Daily Specials', icon: <FaStar /> },
  { label: 'Catering Available', icon: <FaUtensils /> },
  { label: 'South Park, San Diego', icon: <MdLocationOn /> },
];

function Marquee() {
  return (
    <div className="marquee-strip">
      <div className="marquee-inner">
        {[...marqueeItems, ...marqueeItems].map((item, i) => (
          <span key={i}><span className="marquee-icon">{item.icon}</span>{item.label}</span>
        ))}
      </div>
    </div>
  );
}

// ── ABOUT ──
function About() {
  return (
    <section className="about" id="about">
      <FadeIn className="about-img-wrap">
        <div className="about-img-bg">
          <img src="/store.webp" alt="Food Bowl Market storefront in South Park, San Diego" />
        </div>
        <div className="about-badge">
          <div className="big">35+</div>
          <div className="small">Years Serving SD</div>
        </div>
      </FadeIn>
      <div className="about-body">
        <div className="section-eyebrow"><FaStar /> Our Story</div>
        <h2 className="section-title">A <em>neighborhood gem</em> since 1989</h2>
        <p>Food Bowl Market began as a humble Mexican market over 35 years ago, and has been dedicated to providing fresh foods and quality ingredients to the families of South Park and Golden Hill ever since.</p>
        <p>As our community grew, we grew with it. Today we offer the most popular national brands alongside organic and healthy alternatives, hundreds of craft beers and wines, a full-service butcher counter, and a beloved deli — all without the crazy parking or long lines of corporate chains.</p>
        <p>We're not just a grocery store. We're neighbors, and we treat every customer like family.</p>
        <div className="about-stats">
          {[['1989','Established'],['500+','Craft Beers & Wines'],['100%','Community Focused']].map(([num, label]) => (
            <div key={label}>
              <div className="stat-num">{num}</div>
              <div className="stat-label">{label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── OFFERINGS ──
const offerings = [
  { icon: <FaShoppingBasket />, title: 'Fresh Produce', desc: 'Locally sourced and organic fruits and vegetables stocked fresh daily, from everyday staples to seasonal specialties.' },
  { icon: <GiMeat />, title: 'Quality Meats', desc: 'Full butcher counter with premium cuts, featuring USDA Choice, and expert advice for every cook.' },
  { icon: <FaBeer />, title: 'Craft Beer & Wine', desc: 'Hundreds of craft beers spanning local San Diego breweries to international favorites, plus a curated wine selection.' },
  { icon: <FaLeaf />, title: 'Organic & Healthy', desc: 'A growing selection of organic alternatives, health-focused packaged goods, and specialty diet items.' },
  { icon: <FaHome />, title: 'Household Essentials', desc: 'From cleaning supplies to medicine, toiletries, pet food, and everyday essentials — everything you need without the big box store hassle.' },
  { icon: <FaGlobeAmericas />, title: 'Ethnic & Specialty', desc: "A vibrant variety of international and ethnic products reflecting San Diego's rich cultural diversity." },
];

function Offerings() {
  return (
    <section className="offerings" id="offerings">
      <div className="offerings-header">
        <div className="section-eyebrow" style={{justifyContent:'center'}}><FaShoppingBasket /> What We Carry</div>
        <h2 className="section-title">Everything you need,<br /><em>nothing you don't</em></h2>
      </div>
      <div className="offerings-grid">
        {offerings.map(({ icon, title, desc }) => (
          <FadeIn key={title} className="offer-card">
            <span className="offer-icon">{icon}</span>
            <h3>{title}</h3>
            <p>{desc}</p>
          </FadeIn>
        ))}
      </div>
    </section>
  );
}

// ── DELI ──
const deliItems = [
  { icon: <GiSandwich />, name: 'Deli Sandwiches', tag: 'Hot & Cold' },
  { icon: <GiTacos />, name: 'Tacos', tag: 'Hot Food' },
  { icon: <GiTacos />, name: 'Burritos', tag: 'Hot Food' },
  { icon: <GiTacos />, name: 'Tortas', tag: 'Hot Food' },
  { icon: <GiTacos />, name: 'Fresh Chips', tag: 'House-Made' },
  { icon: <GiAvocado />, name: 'Fresh Guacamole', tag: 'House-Made' },
  { icon: <FaPepperHot />, name: 'Fresh Salsa', tag: 'House-Made' },
];

function Deli() {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://www.instagram.com/embed.js';
    script.async = true;
    script.onload = () => {
      if (window.instgrm) window.instgrm.Embeds.process();
    };
    document.body.appendChild(script);
  }, []);

  return (
    <section className="deli" id="deli">
      <div className="deli-body">
        <div className="section-eyebrow deli-eyebrow"><FaUtensils /> FBM Delicatessen</div>
        <h2 className="section-title deli-title">Made fresh,<br /><em>every single day</em></h2>
        <p className="deli-desc">Our full-service hot food and deli is the heart of Food Bowl Market. Whether you're grabbing a quick lunch or picking up food for a group, we've got something delicious ready for you.</p>
        <div className="deli-menu-items">
          {deliItems.map(({ icon, name, tag }) => (
            <div key={name} className="deli-item">
              <span className="deli-item-icon">{icon}</span>
              <span className="deli-item-name">{name}</span>
              <span className="deli-item-tag">{tag}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="ig-wrap">
        <div className="ig-header">
          <span className="ig-label"><FaInstagram /> @foodbowlsouthpark</span>
          <a href="https://www.instagram.com/foodbowlsouthpark/" target="_blank" rel="noreferrer" className="ig-follow">Follow Us</a>
        </div>
        <div className="ig-grid">
          {[
            'https://www.instagram.com/p/CtCA934OXPV/',
            'https://www.instagram.com/p/DG83xXXyd3x/',
            'https://www.instagram.com/p/C328waRIIEG/',
            'https://www.instagram.com/p/CoYfOyvNwRs/',
          ].map(url => (
            <blockquote
              key={url}
              className="instagram-media ig-post"
              data-instgrm-permalink={url}
              data-instgrm-version="14"
            />
          ))}
        </div>
      </div>
    </section>
  );
}

// ── ORDER ──
function Order() {
  return (
    <section className="order-section">
      <h2>Order Online for Delivery</h2>
      <p>Get your Food Bowl favorites delivered right to your door. Order through Grubhub.</p>
      <a href="https://www.grubhub.com/restaurant/food-bowl-market-and-deli-3035-cedar-st-san-diego/1660382" target="_blank" rel="noreferrer" className="btn-white">
        <MdDeliveryDining /> Order on Grubhub
      </a>
    </section>
  );
}

// ── CATERING ──
const cateringItems = [
  { icon: <GiTacos />, title: 'Taco Platters', desc: 'Perfect for any gathering — customize with your choice of proteins and toppings.' },
  { icon: <GiSandwich />, title: 'Deli Sandwich Trays', desc: 'Assorted cold and hot sandwiches, sliced and ready for your event.' },
  { icon: <FaUtensils />, title: 'Party Pinwheels', desc: 'Our signature rolled pinwheels — always a crowd-pleaser at any event.' },
];

function Catering() {
  return (
    <section className="catering" id="catering">
      <div>
        <div className="section-eyebrow"><FaUtensils /> Catering</div>
        <h2 className="section-title">Feed your <em>crowd</em> with Food Bowl</h2>
        <p style={{color:'var(--text-mid)',lineHeight:1.8,marginBottom:28}}>Hosting an event? We offer catering options that bring Food Bowl to your party, office, or gathering. Call us to discuss your needs.</p>
        <a href="tel:6192394709" className="btn-primary"><MdPhone /> Call to Book Catering</a>
      </div>
      <div className="catering-cards">
        {cateringItems.map(({ icon, title, desc }) => (
          <FadeIn key={title} className="catering-card">
            <span className="catering-card-icon">{icon}</span>
            <div><h4>{title}</h4><p>{desc}</p></div>
          </FadeIn>
        ))}
      </div>
    </section>
  );
}

// ── INFO ──
const hours = [
  ['Monday','8:00 AM – 9:00 PM'],
  ['Tuesday','8:00 AM – 9:00 PM'],
  ['Wednesday','8:00 AM – 9:00 PM'],
  ['Thursday','8:00 AM – 9:00 PM'],
  ['Friday','8:00 AM – 9:00 PM'],
  ['Saturday','8:00 AM – 9:00 PM'],
  ['Sunday','8:00 AM – 8:00 PM'],
];

function Info() {
  return (
    <section className="info-section" id="info">
      <div className="hours-card">
        <h3><FaUtensils style={{color:'var(--red)'}} /> Store Hours</h3>
        <div className="open-badge"><span className="open-dot" /> Open Today</div>
        {hours.map(([day, time]) => (
          <div key={day} className="hours-row">
            <span className="hours-day">{day}</span>
            <span className="hours-time">{time}</span>
          </div>
        ))}
      </div>
      <div className="contact-info">
        <div className="section-eyebrow"><MdLocationOn /> Find Us</div>
        <h2 className="section-title contact-title">Come <em>say hello</em></h2>
        <FadeIn className="contact-card">
          <div className="contact-icon"><MdLocationOn /></div>
          <div>
            <h4>Address</h4>
            <a href="https://maps.google.com/?q=3035+Cedar+St,+San+Diego,+CA+92102" target="_blank" rel="noreferrer">3035 Cedar St<br />San Diego, CA 92102</a>
          </div>
        </FadeIn>
        <FadeIn className="contact-card">
          <div className="contact-icon"><MdPhone /></div>
          <div>
            <h4>Phone</h4>
            <a href="tel:6192394709">(619) 239-4709</a>
          </div>
        </FadeIn>
        <FadeIn className="contact-card">
          <div className="contact-icon"><FaInstagram /></div>
          <div>
            <h4>Instagram</h4>
            <a href="https://www.instagram.com/foodbowlsouthpark/" target="_blank" rel="noreferrer">@foodbowlsouthpark</a>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

// ── FOOTER ──
function Footer() {
  return (
    <footer>
      <div className="footer-top">
        <div className="footer-brand">
          <img src="/logo.png" alt="Food Bowl Market" />
          <p>South Park's neighborhood grocer and deli, proudly serving San Diego families with quality food and ingredients for healthy living since 1989.</p>
        </div>
        <div className="footer-col">
          <h4>Navigate</h4>
          <ul>
            {[['#about','Our Story'],['#offerings','What We Carry'],['#deli','Hot Food & Deli'],['#catering','Catering'],['#info','Hours & Contact']].map(([href,label]) => (
              <li key={href}><a href={href}>{label}</a></li>
            ))}
          </ul>
        </div>
        <div className="footer-col">
          <h4>Links</h4>
          <ul>
            <li><a href="https://www.grubhub.com/restaurant/food-bowl-market-and-deli-3035-cedar-st-san-diego/1660382" target="_blank" rel="noreferrer">Grubhub</a></li>
            <li><a href="https://www.yelp.com/biz/food-bowl-market-san-diego-2" target="_blank" rel="noreferrer">Yelp Reviews</a></li>
            <li><a href="https://www.facebook.com/FoodBowlMarketSD/" target="_blank" rel="noreferrer">Facebook</a></li>
            <li><a href="https://www.instagram.com/foodbowlsouthpark/" target="_blank" rel="noreferrer">Instagram</a></li>
          </ul>
        </div>
      </div>
      <div className="footer-bottom">
        <p>© 2026 Food Bowl Market · 3035 Cedar St, San Diego, CA 92102</p>
        <div className="social-links">
          <a href="https://www.facebook.com/FoodBowlMarketSD/" target="_blank" rel="noreferrer" title="Facebook"><FaFacebook /></a>
          <a href="https://www.instagram.com/foodbowlsouthpark/" target="_blank" rel="noreferrer" title="Instagram"><FaInstagram /></a>
          <a href="https://www.yelp.com/biz/food-bowl-market-san-diego-2" target="_blank" rel="noreferrer" title="Yelp"><FaYelp /></a>
        </div>
      </div>
    </footer>
  );
}

// ── APP ──
export default function App() {
  return (
    <>
      <Nav />
      <Hero />
      <Marquee />
      <About />
      <Offerings />
      <Deli />
      <Order />
      <Catering />
      <Info />
      <Footer />
    </>
  );
}
